import React, { Suspense, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { lazyWithRetry } from './lib/lazyWithRetry';
import { canAccessIssueTriage } from './lib/issueTriageAccess';
import { CrossOriginFrameGuard } from './lib/embed/crossOriginFrameGuard';
import { EmbedOnlyApp } from './lib/embed/EmbedOnlyApp';
import { QUERY_CLIENT_DEFAULTS } from './lib/queryRetryPolicy';
import { ModuleRegistry } from './modules/registry';
import { TechnicianProvider } from './contexts/TechnicianContext';
import { PageSkeleton, ModuleErrorBoundary } from './components/shared';
import NotFound from './pages/NotFound';
import { routes as aiSystemsRoutes } from './modules/ai-systems';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { AIProvider } from './contexts/AIContext';
import { ModuleProvider, useModule, type ModuleId } from './contexts/ModuleContext';
import { MapOverlayProvider } from './components/shared/MapOverlay';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ModuleLayout } from './components/ModuleLayout';
import { ModuleGuard } from './components/ModuleGuard';
import { InvestorPortalLayout } from './components/InvestorPortalLayout';
import { init as initContextCapture, setRoute, setEnabled as setContextCaptureEnabled } from './lib/contextCapture';
import { handleCallableHedge, handleConsoleError, handleNetworkError, handleNetworkSuccess, setCurrentRoute, shouldCaptureNetworkErrorBody } from './lib/datahub/errorLogging';
import { trackPageView, setUsageTrackingUser } from './lib/datahub/usageTracking';
import { BugReportWidget, FloatingFeedbackButton } from './components/IssueTriage';
import { floatingWidgetPlan } from './lib/issueTriage/floatingWidgets';
import { AlWidget } from './components/shared/AlWidget';
import { useBugReportShortcut, useIssueTriageFlags } from './hooks/issueTriage';
import { useAuth } from './contexts/AuthContext';
import { CommandPaletteProvider, useCommandPalette } from './contexts/CommandPaletteContext';
import { InvestorPreviewProvider, useInvestorPreview } from './contexts/InvestorPreviewContext';
import { CommandPalette } from './components/shared/CommandPalette';
import { isTypesenseEnabled } from './config/typesense';
import { useModuleChunkPrefetch } from './hooks/core/useModuleChunkPrefetch';
import { SyncProvider, SyncBadge } from '@officehub/offline-sdk';
import { surveySyncEngine } from './lib/survey/offlineEngine';
import { AppVersion } from './appVersion';
import type { VersionInfo } from './appVersion';
import appVersionInfo from './generated/appVersion.json';
import { useWakeScreen } from './hooks/useWakeScreen';
import { useKeepWarm } from './hooks/useKeepWarm';
import { WAKE_APP_SHELL } from './config/wake/appShell';

// Lazy-loaded page components (wrapped with retry to auto-recover from ChunkLoadError)
const Login = lazyWithRetry(() => import('./pages/Login').then(m => ({ default: m.Login })));
const ConnectClaudeCode = lazyWithRetry(() => import('./pages/ConnectClaudeCode').then(m => ({ default: m.ConnectClaudeCode })));
const Home = lazyWithRetry(() => import('./pages/Home').then(m => ({ default: m.Home })));
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Profile = lazyWithRetry(() => import('./pages/Profile').then(m => ({ default: m.Profile })));
const MyVehicle = lazyWithRetry(() => import('./pages/Driver/MyVehicle').then(m => ({ default: m.MyVehicle })));
const DecisionPage = lazyWithRetry(() => import('./pages/Decisions/DecisionPage').then(m => ({ default: m.DecisionPage })));
const InspectionSubmitPage = lazyWithRetry(() => import('./pages/Fleet/InspectionSubmit'));
const OrganizationManagement = lazyWithRetry(() => import('./pages/OrganizationManagement').then(m => ({ default: m.OrganizationManagement })));
// Quoting pages (Quotes, Customers, Equipment) now lazy-loaded via ModuleRegistry
const ServicePartsList = lazyWithRetry(() => import('./pages/ServiceParts/ServicePartsList').then(m => ({ default: m.ServicePartsList })));
const ServicePartsDetail = lazyWithRetry(() => import('./pages/ServiceParts/ServicePartsDetail').then(m => ({ default: m.ServicePartsDetail })));
const Inventory = lazyWithRetry(() => import('./pages/ServiceParts/Inventory').then(m => ({ default: m.Inventory })));
const PriceBookDemo = lazyWithRetry(() => import('./pages/PriceBookDemo').then(m => ({ default: m.PriceBookDemo })));
// PriceBook now lazy-loaded via ModuleRegistry
const PublicQuoteView = lazyWithRetry(() => import('./pages/Public/PublicQuoteView').then(m => ({ default: m.PublicQuoteView })));
const CustomerFacingQuote = lazyWithRetry(() => import('./pages/Quotes/CustomerFacingQuote').then(m => ({ default: m.CustomerFacingQuote })));
const CustomerFacingInvoice = lazyWithRetry(() => import('./pages/Invoices/CustomerFacingInvoice').then(m => ({ default: m.CustomerFacingInvoice })));
const AcceptInvitation = lazyWithRetry(() => import('./pages/AcceptInvitation').then(m => ({ default: m.AcceptInvitation })));
const ProductAnalytics = lazyWithRetry(() => import('./pages/ErrorTracking/ProductAnalytics').then(m => ({ default: m.ProductAnalytics })));
const ServiceAnalytics = lazyWithRetry(() => import('./pages/ServiceAnalytics').then(m => ({ default: m.ServiceAnalytics })));
// Prospect pages removed — module fully migrated to CRM Leads
const PipelineView = lazyWithRetry(() => import('./pages/Pipeline/PipelineView').then(m => ({ default: m.PipelineView })));
const DealDetail = lazyWithRetry(() => import('./pages/Pipeline/DealDetail').then(m => ({ default: m.DealDetail })));
// All module About pages now lazy-loaded via ModuleRegistry
// Support pages now lazy-loaded via ModuleRegistry
// CRM pages now lazy-loaded via ModuleRegistry
// Forms pages now lazy-loaded via ModuleRegistry
// Collections pages now lazy-loaded via ModuleRegistry
const UserSystemLinking = lazyWithRetry(() => import('./pages/Admin/UserSystemLinking').then(m => ({ default: m.UserSystemLinking })));
// QuotingDashboard now lazy-loaded via ModuleRegistry
// DataHub/Explorer pages now lazy-loaded via ModuleRegistry
// Fleet pages now lazy-loaded via ModuleRegistry
const Privacy = lazyWithRetry(() => import('./pages/Legal').then(m => ({ default: m.Privacy })));
const Terms = lazyWithRetry(() => import('./pages/Legal').then(m => ({ default: m.Terms })));

// Lazy-loaded admin pages (wrapped with retry to auto-recover from ChunkLoadError)
const PeopleManagement = lazyWithRetry(() => import('./pages/Admin/PeopleManagement'));
const IssueTriage = lazyWithRetry(() => import('./pages/Admin/IssueTriage'));
const ExpiredQueuedReport = lazyWithRetry(() => import('./pages/Admin/ExpiredQueuedReport'));
const DocumentTemplates = lazyWithRetry(() => import('./pages/Admin/DocumentTemplates'));
const WorkshopGrantsAdmin = lazyWithRetry(() => import('./pages/Admin/WorkshopGrants'));
const DriveSettings = lazyWithRetry(() => import('./pages/Admin/DriveSettings'));
const DocumentTemplateEditor = lazyWithRetry(() => import('./pages/Admin/DocumentTemplates/Editor'));
const GeneratorQboMatcher = lazyWithRetry(() => import('./pages/Admin/GeneratorQboMatcher'));
const ConfigureGetAQuoteForm = lazyWithRetry(() => import('./pages/Admin/ConfigureGetAQuoteForm'));
const ConfigureFleetInspectionForm = lazyWithRetry(() => import('./pages/Admin/ConfigureFleetInspectionForm'));
const PublicLinkDiagnostic = lazyWithRetry(() => import('./pages/Admin/PublicLinkDiagnostic'));
const AIIntelligence = lazyWithRetry(() => import('./pages/Admin/AIIntelligence'));
const AICosts = lazyWithRetry(() => import('./pages/Admin/AICosts/AICosts'));
const LauncherAdmin = lazyWithRetry(() => import('./pages/Admin/Launcher/LauncherAdmin'));
const CollectionsBackfill = lazyWithRetry(() => import('./pages/Admin/CollectionsBackfill'));
const EmailTemplatesAdmin = lazyWithRetry(() => import('./pages/Admin/EmailTemplates'));
const ProjectMigration = lazyWithRetry(() => import('./pages/Admin/ProjectMigration'));
const TechProductionReview = lazyWithRetry(() => import('./pages/Admin/TechProductionReview'));
const TechnicianCostRates = lazyWithRetry(() => import('./components/Admin/profitability/TechnicianCostRatesEditor'));
const RoleTagsPage = lazyWithRetry(() => import('./pages/Admin/RoleTags/RoleTagsPage'));
const EmailHealth = lazyWithRetry(() => import('./pages/Admin/EmailHealth'));
const SharedReportPage = lazyWithRetry(() => import('./pages/Forms/SharedReportPage'));
const PublicComplianceUpdateForm = lazyWithRetry(() => import('./pages/Fleet/PublicComplianceUpdateForm'));
const PublicReferralForm = lazyWithRetry(() => import('./pages/Public/PublicReferralForm'));
const DocumentRequestPage = lazyWithRetry(() => import('./pages/Documents/DocumentRequestPage'));
const MyDocumentRequests = lazyWithRetry(() => import('./pages/Documents/MyDocumentRequests'));
const LauncherPage = lazyWithRetry(() => import('./pages/Launcher').then(m => ({ default: m.LauncherPage })));

// Survey pages
const SurveyCapture = lazyWithRetry(() => import('./pages/Surveys/SurveyCapture').then(m => ({ default: m.SurveyCapture })));
const NewSurvey = lazyWithRetry(() => import('./pages/Surveys/NewSurvey').then(m => ({ default: m.NewSurvey })));
const MobileSurveyCapture = lazyWithRetry(() => import('./pages/Surveys/MobileSurveyCapture').then(m => ({ default: m.MobileSurveyCapture })));
const ReportWizard = lazyWithRetry(() => import('./pages/Surveys/ReportWizard').then(m => ({ default: m.ReportWizard })));

// Legacy route redirect components (captures route params)
function LegacyQuoteRedirect() {
  const { quoteId } = useParams();
  return <Navigate to={`/quoting/quotes/${quoteId}`} replace />;
}

function LegacyEquipmentRedirect() {
  const { equipmentId } = useParams();
  return <Navigate to={`/quoting/equipment/${equipmentId}`} replace />;
}

/** Redirect /start-report?serviceId=X → /forms/start-report?serviceId=X */
function StartReportRedirect() {
  const location = useLocation();
  return <Navigate to={`/forms/start-report${location.search}`} replace />;
}

/** Redirect /fleet/* → /assets/* for legacy bookmark compatibility */
function FleetLegacyRedirect() {
  const location = useLocation();
  const newPath = location.pathname.replace(/^\/fleet/, '/assets');
  return <Navigate to={`${newPath}${location.search}`} replace />;
}

const queryClient = new QueryClient({ defaultOptions: QUERY_CLIENT_DEFAULTS });

/**
 * AppContent - Inner component that uses router hooks
 * Separated because useLocation requires Router context
 */
// Widget positions are now managed independently inside AlWidget and FloatingFeedbackButton.

/**
 * InvestorLayoutSwitcher — uses branded portal for pure Investor role,
 * standard ModuleLayout (with top bar + module selector) for employees.
 *
 * An Owner previewing the portal ("view as investor") gets the branded portal
 * chrome too — the header and sidebar are part of what the investor sees, so a
 * preview inside the employee top bar would not be the thing being checked.
 * The preview banner rides above it with the exit control.
 */
function InvestorLayoutSwitcher({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { isPreviewing } = useInvestorPreview();
  if (user?.role === 'Investor' || isPreviewing) {
    return <InvestorPortalLayout>{children}</InvestorPortalLayout>;
  }
  return <ModuleLayout>{children}</ModuleLayout>;
}

/**
 * AuthedOfflineSync — mounts the durable survey save/sync engine (@officehub/
 * offline-sdk) ONLY for signed-in users. This is the SURVEY outbox
 * (surveySyncEngine, client/src/lib/survey/offlineEngine.ts) — NOT the forms
 * outbox (client/src/lib/forms/offline/formsOutbox.ts), which is a separate
 * engine consumed by client-tech and has no UI wired to it yet. SyncProvider
 * starts the sync triggers (interval / online / visibility) that drain the
 * outbox and retry failures; SyncBadge surfaces pending/failed counts, with
 * tap-to-sync on pending and a Retry-all/Discard panel on failed. Both are
 * gated on auth so a logged-out tab never runs sync. Rendered inside the
 * auth/org providers (AppContent) so the engine's callables carry the user's
 * auth.
 */
function AuthedOfflineSync({
  authed,
  children,
}: {
  authed: boolean;
  children: React.ReactNode;
}) {
  if (!authed) return <>{children}</>;
  return (
    <SyncProvider engine={surveySyncEngine}>
      {children}
      {/* Unobtrusive floating sync status — self-hides when nothing is pending.
          panelPlacement="above-left": this badge sits at the bottom-left of the
          viewport, so the failed-uploads panel must open ABOVE it (below-full
          would render under the fold) and anchor LEFT (right-anchored would
          extend the panel off-screen to the left, since the badge itself sits
          near the left edge). */}
      <div className="fixed bottom-3 left-3 z-40 print:hidden">
        <SyncBadge panelPlacement="above-left" />
      </div>
    </SyncProvider>
  );
}

// Warms the callables used later in the session (first search, Drive, email, FSR review) once per app load.
function AppContent() {
  useWakeScreen(WAKE_APP_SHELL.appShell);
  const { currentModule, accessibleModules, hasModuleAccess, userModules } = useModule();
  const location = useLocation();
  // L-0868: while the app is open, keep warm every accessible module's landing list, the current module's nav and sub-nav
  // pages, the pages one click from this one, and the calls every screen makes (lists in config/wake), only for modules the user can open.
  useKeepWarm(userModules, currentModule, hasModuleAccess, location.pathname);
  // Bug report widget state
  const { isWidgetOpen, openWidget, closeWidget } = useBugReportShortcut();
  // Global search — state lives in CommandPaletteProvider above all module error boundaries
  const { isOpen: searchOpen, close: closeSearch } = useCommandPalette();

  // Incoming call notification banner — mode controls when it shows
  const { user } = useAuth();
  // Screen pop disabled — replaced by lightweight DialpadCallFeed widget on Support Inbox
  // const callMode = user?.preferences?.callNotificationMode ?? 'global';
  // const isOnSupportRoute = location.pathname.startsWith('/support');
  // const globalIncomingCall = useIncomingCall({
  //   enabled: !!user && (callMode === 'global' || (callMode === 'support_only' && isOnSupportRoute)),
  // });

  // Feature flags
  const issueTriageFlags = useIssueTriageFlags();

  // Pre-fetch JS chunks for all modules the user can access (instant switching)
  const accessibleModuleIds = useMemo(
    () => accessibleModules.map((m) => m.id as ModuleId),
    [accessibleModules],
  );
  useModuleChunkPrefetch(accessibleModuleIds);

  // Initialize context capture on mount, wiring error reporting callbacks
  useEffect(() => {
    initContextCapture({
      onConsoleError: handleConsoleError,
      onNetworkError: handleNetworkError,
      shouldCaptureErrorBody: shouldCaptureNetworkErrorBody,
      onNetworkSuccess: handleNetworkSuccess,
      onCallableHedge: handleCallableHedge,
    });
  }, []);

  // Respect feature flag for context capture
  useEffect(() => {
    setContextCaptureEnabled(issueTriageFlags.contextCaptureEnabled);
  }, [issueTriageFlags.contextCaptureEnabled]);
  useEffect(() => {
    setRoute(location.pathname);
    setCurrentRoute(location.pathname);
    trackPageView(location.pathname);
  }, [location.pathname]);

  // Sync auth user into usage tracking
  useEffect(() => {
    if (user) {
      setUsageTrackingUser(user.userId, user.email);
    } else {
      setUsageTrackingUser(undefined, undefined);
    }
  }, [user]);

  // Floating widgets per route: none on embed/public pages; the feedback button docks mid-right on issue triage.
  const widgets = floatingWidgetPlan(location.pathname);

  return (
    <AuthedOfflineSync authed={!!user}>
      {/* Screen pop banner disabled — using DialpadCallFeed widget instead */}

      <Toaster
        position="top-right"
        containerStyle={{ top: 72 }} // nav h-14 (56px) + 1px border + 15px breathing room
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10B981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#EF4444',
              secondary: '#fff',
            },
          },
        }}
      />

      <Routes>
        {/* Public Routes */}
        <Route path="/public/quote/:token" element={
          <React.Suspense fallback={<PageSkeleton />}>
            <PublicQuoteView />
          </React.Suspense>
        } />
        <Route path="/public/fleet/compliance-update/:token" element={
          <React.Suspense fallback={<PageSkeleton />}>
            <PublicComplianceUpdateForm />
          </React.Suspense>
        } />
        <Route path="/public/recruiting/referral/:token" element={
          <React.Suspense fallback={<PageSkeleton />}>
            <PublicReferralForm />
          </React.Suspense>
        } />
        <Route path="/quotes/:quoteId/public" element={
          <React.Suspense fallback={<PageSkeleton />}>
            <CustomerFacingQuote />
          </React.Suspense>
        } />
        <Route path="/invoices/:invoiceId" element={
          <React.Suspense fallback={<PageSkeleton />}>
            <CustomerFacingInvoice />
          </React.Suspense>
        } />
        <Route path="/invite/:token" element={
          <React.Suspense fallback={<PageSkeleton />}>
            <AcceptInvitation />
          </React.Suspense>
        } />
        <Route path="/login" element={
          <React.Suspense fallback={<PageSkeleton />}>
            <Login />
          </React.Suspense>
        } />
        <Route path="/connect-claude-code" element={
          <React.Suspense fallback={<PageSkeleton />}>
            <ConnectClaudeCode />
          </React.Suspense>
        } />


        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <Home />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />

        {/* Legacy Dashboard - redirects to home for now */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <Dashboard />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <Profile />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />

        {/* "My vehicle" (drivers): NOT a module and not gated on P7. Every callable is self-scoped on the server. */}
        <Route
          path="/my-vehicle"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <MyVehicle />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        {/* "Decision": answer an agent's request. NOT a module and not gated on AI_SYSTEMS: the server decides who may answer
            (the module that owns the action + the approver chain), so every link from Chat or email can point here. */}
        <Route
          path="/decisions/:taskId"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <DecisionPage />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-vehicle/inspect"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <InspectionSubmitPage />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/organization"
          element={
            <ProtectedRoute requireManagerOrAdmin>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <OrganizationManagement />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />

        {/* Quoting Module Routes (P2) - via Module Registry */}
        <Route path="/quoting/*" element={
          <ModuleErrorBoundary moduleName="Quoting">
            <ModuleLayout>
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  {ModuleRegistry.getById('P2')?.routes.map((route, index) => (
                    <Route
                      key={index}
                      path={route.path ?? ''}
                      index={route.index}
                      element={route.element}
                    />
                  ))}
                </Routes>
              </Suspense>
            </ModuleLayout>
          </ModuleErrorBoundary>
        } />

        {/* Legacy redirects for bookmarks */}
        <Route path="/quotes" element={<Navigate to="/quoting/quotes" replace />} />
        <Route path="/quotes/create" element={<Navigate to="/quoting/quotes/create" replace />} />
        <Route path="/quotes/:quoteId" element={<LegacyQuoteRedirect />} />
        <Route path="/customers" element={<Navigate to="/quoting/customers" replace />} />
        <Route path="/equipment" element={<Navigate to="/quoting/equipment" replace />} />
        <Route path="/equipment/:equipmentId" element={<LegacyEquipmentRedirect />} />
        <Route path="/price-book" element={<Navigate to="/quoting/price-book" replace />} />
        <Route path="/start-report" element={<StartReportRedirect />} />

        {/* Service Parts Routes (keeping separate for now) */}
        <Route
          path="/service-parts"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <ServicePartsList />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/service-parts/:servicePartId"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <ServicePartsDetail />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />

        {/* Inventory Route */}
        <Route
          path="/inventory"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <Inventory />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />

        {/* Price Book Demo */}
        <Route
          path="/price-book-demo"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <PriceBookDemo />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />

        {/* Service Analytics Route */}
        <Route
          path="/service-analytics"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <ServiceAnalytics />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />

        {/* Error Tracking Route (restricted to authorized personnel) */}
        <Route
          path="/error-tracking"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <ProductAnalytics />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />

        {/* Prospecting routes removed — module fully migrated to CRM Leads */}

        {/* Pipeline/CRM Routes */}
        <Route
          path="/pipeline"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <PipelineView />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/pipeline/:dealId"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <DealDetail />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />

        {/* CRM Routes (P1) - via Module Registry */}
        <Route path="/crm/*" element={
          <ProtectedRoute>
            <ModuleErrorBoundary moduleName="Sales (CRM)">
              <ModuleLayout>
                <ModuleGuard moduleId="P1">
                  <Suspense fallback={<PageSkeleton />}>
                    <Routes>
                      {ModuleRegistry.getById('P1')?.routes.map((route, index) => (
                        <Route
                          key={index}
                          path={route.path ?? ''}
                          index={route.index}
                          element={route.element}
                        />
                      ))}
                    </Routes>
                  </Suspense>
                </ModuleGuard>
              </ModuleLayout>
            </ModuleErrorBoundary>
          </ProtectedRoute>
        } />
        {/* Survey Routes */}
        {/* Quick mobile capture — /survey/new must precede /survey/:surveyId */}
        <Route
          path="/survey/new"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <NewSurvey />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/survey/:surveyId"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <MobileSurveyCapture />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/surveys/:surveyId/report"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <ReportWizard />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/surveys/:surveyId/capture"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <SurveyCapture />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/project-migration"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <ProjectMigration />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <PeopleManagement />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route path="/admin/module-access" element={<Navigate to="/admin/users" replace />} />
        <Route
          path="/admin/user-linking"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <UserSystemLinking />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/issue-triage"
          element={
            <ProtectedRoute allowUser={canAccessIssueTriage}>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <IssueTriage />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/issue-triage/:issueCode"
          element={
            <ProtectedRoute allowUser={canAccessIssueTriage}>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <IssueTriage />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        {/* Saved copy of a report a tech phone could not send for 30 days (the office
            notice links here). Admin/Owner only; the callable enforces it too. */}
        <Route
          path="/admin/expired-reports/:submissionId"
          element={
            <ProtectedRoute requiredRole={['Admin', 'Owner']}>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <ExpiredQueuedReport />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/document-templates"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <DocumentTemplates />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/workshop/*"
          element={
            <ProtectedRoute requireManagerOrAdmin>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <WorkshopGrantsAdmin />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/document-templates/new"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <DocumentTemplateEditor />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/document-templates/:templateId/edit"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <DocumentTemplateEditor />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/drive-settings"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <DriveSettings />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/generator-qbo-matcher"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <GeneratorQboMatcher />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/configure-get-a-quote-form"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <ConfigureGetAQuoteForm />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/public-link-diagnostic"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <PublicLinkDiagnostic />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/configure-fleet-inspection-form"
          element={
            <ProtectedRoute requireManagerOrAdmin>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <ConfigureFleetInspectionForm />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/ai-intelligence"
          element={
            <ProtectedRoute requireManagerOrAdmin>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <AIIntelligence />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/ai-costs"
          element={
            <ProtectedRoute requiredRole={['Admin', 'Owner']}>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <AICosts />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/launcher"
          element={
            <ProtectedRoute requiredRole={['Admin', 'Owner', 'Manager']}>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <LauncherAdmin />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/collections-backfill"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <CollectionsBackfill />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/email-templates"
          element={
            <ProtectedRoute requireAdmin>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <EmailTemplatesAdmin />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/tech-production-review"
          element={
            <ProtectedRoute requiredRole={['Admin', 'Owner']}>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <TechProductionReview />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/tech-cost-rates"
          element={
            <ProtectedRoute requireManagerOrAdmin>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <TechnicianCostRates />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/role-tags"
          element={
            <ProtectedRoute requireAdmin>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <RoleTagsPage />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/email-health"
          element={
            <ProtectedRoute requireAdmin>
              <ModuleLayout>
                <React.Suspense fallback={<PageSkeleton />}>
                  <EmailHealth />
                </React.Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />

        {/* Legacy redirect for quoting about — the About page itself was removed
            (ISS-469); an old bookmark now lands on the Quoting dashboard. */}
        <Route path="/quotes/about" element={<Navigate to="/quoting" replace />} />

        {/* P3 Contracts merged into P11 Field Service — /contracts redirects */}
        <Route path="/contracts/*" element={<Navigate to="/field-service/contracts" replace />} />

        {/* Billing & Collections Routes (P4) - via Module Registry */}
        {/* #3989 (Nick 2026-10-20: finance first): the Finance license is a real boundary for this screen. Measured before wiring: of 25
            active internal people without P4, 0 opened /billing in the last 90 days. */}
        <Route path="/billing/*" element={
          <ProtectedRoute>
            <ModuleErrorBoundary moduleName="Finance">
              <ModuleLayout>
                <ModuleGuard moduleId="P4">
                  <Suspense fallback={<PageSkeleton />}>
                    <Routes>
                      {ModuleRegistry.getById('P4')?.routes.map((route, index) => (
                        <Route
                          key={index}
                          path={route.path ?? ''}
                          index={route.index}
                          element={route.element}
                        />
                      ))}
                    </Routes>
                  </Suspense>
                </ModuleGuard>
              </ModuleLayout>
            </ModuleErrorBoundary>
          </ProtectedRoute>
        } />

        {/* Customer Support Routes (P5) - via Module Registry */}
        <Route path="/support/*" element={
          <ModuleErrorBoundary moduleName="Support">
            <ModuleLayout>
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  {ModuleRegistry.getById('P5')?.routes.map((route, index) => (
                    <Route
                      key={index}
                      path={route.path ?? ''}
                      index={route.index}
                      element={route.element}
                    />
                  ))}
                </Routes>
              </Suspense>
            </ModuleLayout>
          </ModuleErrorBoundary>
        } />

        {/* Central Command Routes (P6) - via Module Registry */}
        <Route path="/command/*" element={
          <ModuleErrorBoundary moduleName="Central Command">
            <ModuleLayout>
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  {ModuleRegistry.getById('P6')?.routes.map((route, index) => (
                    <Route
                      key={index}
                      path={route.path ?? ''}
                      index={route.index}
                      element={route.element}
                    />
                  ))}
                </Routes>
              </Suspense>
            </ModuleLayout>
          </ModuleErrorBoundary>
        } />

        {/* Legacy /explorer redirect → /command */}
        <Route path="/explorer/*" element={<Navigate to="/command" replace />} />

        {/* Assets Management Routes (P7) - via Module Registry */}
        <Route path="/assets/*" element={
          <ModuleErrorBoundary moduleName="Assets">
            <ModuleLayout>
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  {ModuleRegistry.getById('P7')?.routes.map((route, index) => (
                    <Route
                      key={index}
                      path={route.path ?? ''}
                      index={route.index}
                      element={route.element}
                    />
                  ))}
                </Routes>
              </Suspense>
            </ModuleLayout>
          </ModuleErrorBoundary>
        } />

        {/* Legacy Fleet URL redirects → Assets */}
        <Route path="/fleet" element={<Navigate to="/assets" replace />} />
        <Route path="/fleet/*" element={<FleetLegacyRedirect />} />

        {/* Project Management Routes (P10) - via Module Registry */}
        <Route path="/projects/*" element={
          <ModuleErrorBoundary moduleName="Projects">
            <ModuleLayout>
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  {ModuleRegistry.getById('P10')?.routes.map((route, index) => (
                    <Route
                      key={index}
                      path={route.path ?? ''}
                      index={route.index}
                      element={route.element}
                    />
                  ))}
                </Routes>
              </Suspense>
            </ModuleLayout>
          </ModuleErrorBoundary>
        } />

        {/* Field Service Routes (P11) - via Module Registry */}
        <Route path="/field-service/*" element={
          <ModuleErrorBoundary moduleName="Field Service">
            <ModuleLayout>
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  {ModuleRegistry.getById('P11')?.routes.map((route, index) => (
                    <Route
                      key={index}
                      path={route.path ?? ''}
                      index={route.index}
                      element={route.element}
                    />
                  ))}
                </Routes>
              </Suspense>
            </ModuleLayout>
          </ModuleErrorBoundary>
        } />

        {/* Technician Routes (P12) - via Module Registry, wrapped with TechnicianProvider for impersonation */}
        <Route path="/technician/*" element={
          <ModuleErrorBoundary moduleName="Technician">
            <ModuleLayout>
              <TechnicianProvider>
                <Suspense fallback={<PageSkeleton />}>
                  <Routes>
                    {ModuleRegistry.getById('P12')?.routes.map((route, index) => (
                      <Route
                        key={index}
                        path={route.path ?? ''}
                        index={route.index}
                        element={route.element}
                      />
                    ))}
                  </Routes>
                </Suspense>
              </TechnicianProvider>
            </ModuleLayout>
          </ModuleErrorBoundary>
        } />

        {/* Shared Report Page - public, no auth required.
            This is the ONLY route a customer reaches without logging in, and it
            was the only module route with NO error boundary: any render throw
            (an object where a renderer expected a string - ISS-470) left the
            customer a blank white page with no way forward. ReportField also
            carries a per-field boundary, so a missed leaf degrades to one
            broken field; this is the floor beneath that. */}
        <Route path="/forms/shared/:token" element={
          <ModuleErrorBoundary moduleName="Shared Report">
            <Suspense fallback={<PageSkeleton />}>
              <SharedReportPage />
            </Suspense>
          </ModuleErrorBoundary>
        } />

        {/* Field Service Forms Routes (P8) - via Module Registry */}
        <Route path="/forms/*" element={
          <ModuleErrorBoundary moduleName="Forms">
            <ModuleLayout>
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  {ModuleRegistry.getById('P8')?.routes.map((route, index) => (
                    <Route
                      key={index}
                      path={route.path ?? ''}
                      index={route.index}
                      element={route.element}
                    />
                  ))}
                </Routes>
              </Suspense>
            </ModuleLayout>
          </ModuleErrorBoundary>
        } />

        {/* HR Routes (P9) - via Module Registry */}
        <Route path="/hr/*" element={
          <ModuleErrorBoundary moduleName="HR">
            <ModuleLayout>
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  {ModuleRegistry.getById('P9')?.routes.map((route, index) => (
                    <Route
                      key={index}
                      path={route.path ?? ''}
                      index={route.index}
                      element={route.element}
                    />
                  ))}
                </Routes>
              </Suspense>
            </ModuleLayout>
          </ModuleErrorBoundary>
        } />

        {/* Time Tracking Routes (P13) - via Module Registry */}
        <Route path="/time/*" element={
          <ModuleErrorBoundary moduleName="Time Tracking">
            <ModuleLayout>
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  {ModuleRegistry.getById('P13')?.routes.map((route, index) => (
                    <Route
                      key={index}
                      path={route.path ?? ''}
                      index={route.index}
                      element={route.element}
                    />
                  ))}
                </Routes>
              </Suspense>
            </ModuleLayout>
          </ModuleErrorBoundary>
        } />

        {/* Recruiting Routes (P14) - via Module Registry */}
        <Route path="/recruiting/*" element={
          <ModuleErrorBoundary moduleName="Recruiting">
            <ModuleLayout>
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  {ModuleRegistry.getById('P14')?.routes.map((route, index) => (
                    <Route
                      key={index}
                      path={route.path ?? ''}
                      index={route.index}
                      element={route.element}
                    />
                  ))}
                </Routes>
              </Suspense>
            </ModuleLayout>
          </ModuleErrorBoundary>
        } />

        {/* Marketing Routes (P15) - via Module Registry */}
        <Route path="/marketing/*" element={
          <ModuleErrorBoundary moduleName="Marketing">
            <ModuleLayout>
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  {ModuleRegistry.getById('P15')?.routes.map((route, index) => (
                    <Route
                      key={index}
                      path={route.path ?? ''}
                      index={route.index}
                      element={route.element}
                    />
                  ))}
                </Routes>
              </Suspense>
            </ModuleLayout>
          </ModuleErrorBoundary>
        } />

        {/* Huddle Hub Routes (P16) - via Module Registry */}
        <Route path="/huddle/*" element={
          <ModuleErrorBoundary moduleName="Huddle Hub">
            <ModuleLayout>
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  {ModuleRegistry.getById('P16')?.routes.map((route, index) => (
                    <Route
                      key={index}
                      path={route.path ?? ''}
                      index={route.index}
                      element={route.element}
                    />
                  ))}
                </Routes>
              </Suspense>
            </ModuleLayout>
          </ModuleErrorBoundary>
        } />

        {/* Safety Routes (P17) - via Module Registry */}
        <Route path="/safety/*" element={
          <ModuleErrorBoundary moduleName="Safety">
            <ModuleLayout>
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  {ModuleRegistry.getById('P17')?.routes.map((route, index) => (
                    <Route
                      key={index}
                      path={route.path ?? ''}
                      index={route.index}
                      element={route.element}
                    />
                  ))}
                </Routes>
              </Suspense>
            </ModuleLayout>
          </ModuleErrorBoundary>
        } />

        {/* Workshop Routes (P19) - via Module Registry */}
        <Route path="/workshop/*" element={
          <ModuleErrorBoundary moduleName="Workshop">
            <ModuleLayout>
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  {ModuleRegistry.getById('P19')?.routes.map((route, index) => (
                    <Route
                      key={index}
                      path={route.path ?? ''}
                      index={route.index}
                      element={route.element}
                    />
                  ))}
                </Routes>
              </Suspense>
            </ModuleLayout>
          </ModuleErrorBoundary>
        } />

        {/* Insurance Routes (P20) - via Module Registry */}
        <Route path="/insurance/*" element={
          <ModuleErrorBoundary moduleName="Insurance">
            <ModuleLayout>
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  {ModuleRegistry.getById('P20')?.routes.map((route, index) => (
                    <Route
                      key={index}
                      path={route.path ?? ''}
                      index={route.index}
                      element={route.element}
                    />
                  ))}
                </Routes>
              </Suspense>
            </ModuleLayout>
          </ModuleErrorBoundary>
        } />

        {/* Shop Routes (SHOP) - via Module Registry */}
        <Route path="/shop/*" element={
          <ProtectedRoute>
            <ModuleErrorBoundary moduleName="Shop">
              <ModuleLayout>
                <ModuleGuard moduleId="SHOP">
                  <Suspense fallback={<PageSkeleton />}>
                    <Routes>
                      {ModuleRegistry.getById('SHOP')?.routes.map((route, index) => (
                        <Route
                          key={index}
                          path={route.path ?? ''}
                          index={route.index}
                          element={route.element}
                        />
                      ))}
                    </Routes>
                  </Suspense>
                </ModuleGuard>
              </ModuleLayout>
            </ModuleErrorBoundary>
          </ProtectedRoute>
        } />

        {/* Investor Relations Routes (P18) — layout depends on user role */}
        <Route path="/investors/*" element={
          <InvestorLayoutSwitcher>
            <ModuleErrorBoundary moduleName="Investor Relations">
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  {ModuleRegistry.getById('P18')?.routes.map((route, index) => (
                    <Route
                      key={index}
                      path={route.path ?? ''}
                      index={route.index}
                      element={route.element}
                    />
                  ))}
                </Routes>
              </Suspense>
            </ModuleErrorBoundary>
          </InvestorLayoutSwitcher>
        } />

        {/* AI Systems Routes - Internal admin module */}
        <Route path="/ai-systems/*" element={
          <ModuleErrorBoundary moduleName="AI Systems">
            <ModuleLayout>
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  {aiSystemsRoutes.map((route, index) => (
                    <Route
                      key={index}
                      path={route.path ?? ''}
                      index={route.index}
                      element={route.element}
                    />
                  ))}
                </Routes>
              </Suspense>
            </ModuleLayout>
          </ModuleErrorBoundary>
        } />

        {/* Launcher Dashboard — personalized home screen for field users */}
        <Route
          path="/launcher/*"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <Suspense fallback={<PageSkeleton />}>
                  <LauncherPage />
                </Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />

        {/* Document Request Routes — standalone, available to all authenticated users */}
        <Route
          path="/documents/request"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <Suspense fallback={<PageSkeleton />}>
                  <DocumentRequestPage />
                </Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/documents/my-requests"
          element={
            <ProtectedRoute>
              <ModuleLayout>
                <Suspense fallback={<PageSkeleton />}>
                  <MyDocumentRequests />
                </Suspense>
              </ModuleLayout>
            </ProtectedRoute>
          }
        />

        {/* Legal pages - public, no auth required */}
        <Route path="/privacy" element={
          <React.Suspense fallback={<PageSkeleton />}>
            <Privacy />
          </React.Suspense>
        } />
        <Route path="/terms" element={
          <React.Suspense fallback={<PageSkeleton />}>
            <Terms />
          </React.Suspense>
        } />

        {/* Catch all - an address we cannot route says so and keeps the address */}
        <Route path="*" element={<NotFound />} />
      </Routes>

      {/* Independently draggable widgets — each manages its own position */}
      {widgets.alWidget && <AlWidget />}
      {widgets.feedback && !isWidgetOpen && (
        <FloatingFeedbackButton onClick={openWidget} placement={widgets.feedbackPlacement} />
      )}

      {/* Bug Report Widget - hidden on embed/public pages */}
      {widgets.feedback && <BugReportWidget isOpen={isWidgetOpen} onClose={closeWidget} />}

      {/* Global search — rendered here (above all module error boundaries) so CTRL+K works
          from any module and a CommandPalette crash never takes down the active module. */}
      {isTypesenseEnabled() && (
        <CommandPalette
          open={searchOpen}
          onOpenChange={(open) => { if (!open) closeSearch(); }}
        />
      )}

      {/* Global build-version footer + update detection — one mount, every route.
          Suppressed on embed/public pages (external, non-troubleshooting surfaces). */}
      {widgets.appVersion && (
        <AppVersion current={appVersionInfo as VersionInfo} routeKey={location.pathname} />
      )}
    </AuthedOfflineSync>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <Router>
          {/* Framed by another origin (the public website iframing an embed form):
              /forms/embed/<slug> renders ONLY EmbedOnlyApp (signed-out, no org, no
              nav); any other path renders nothing. The full tree below never mounts
              in a foreign frame. QueryClientProvider and ErrorBoundary stay outside. */}
          <CrossOriginFrameGuard framedEmbed={<EmbedOnlyApp />}>
            <MapOverlayProvider>
              <AuthProvider>
                <OrganizationProvider>
                  <AIProvider>
                    <ModuleProvider>
                      <CommandPaletteProvider>
                        <InvestorPreviewProvider>
                          <AppContent />
                        </InvestorPreviewProvider>
                      </CommandPaletteProvider>
                    </ModuleProvider>
                  </AIProvider>
                </OrganizationProvider>
              </AuthProvider>
            </MapOverlayProvider>
          </CrossOriginFrameGuard>
        </Router>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;
